// TODO: cover this file in proper TypeScript
// @ts-nocheck

import { Formik, FormikActions } from 'formik'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import * as Yup from 'yup'

import { Building } from '@/reducers/types'
import { RootState } from '@/store/hooks'

import { getAssetLink } from '../../../helpers/get-asset-link'
import { useSnackbar } from '../../../helpers/hooks/useSnackbar'
import { selectBuildingsForFilter } from '../../../reducers/buildingSelectorReducer'
import {
  createStaffPerson,
  deleteStaffPerson,
  updateStaffPerson,
} from '../../../reducers/maintenanceStaffReducer'
import {
  createParkingStaff,
  deleteParkingStaffUser,
  updateParkingStaff,
} from '../../../reducers/Organisation/parkingStaffReducer'
import {
  createPropertyManager,
  deletePropertyManager,
  updatePropertyManager,
} from '../../../reducers/Organisation/propertyManagersReducer'
import {
  createShiftManager,
  deleteShiftManager,
  updateShiftManager,
} from '../../../reducers/Organisation/shiftManagersReducer'
import {
  createValet,
  deleteValetUser,
  updateValetUser,
} from '../../../reducers/Organisation/valetReducer'
import DeleteButton from '../../DeleteButton/DeleteButton'
import Filter from '../../Filter/Filter'
import ImageField from '../../FormLegacy/FormFieldsV2/ImageField'
import PhoneField from '../../FormLegacy/FormFieldsV2/PhoneField'
import SelectField from '../../FormLegacy/FormFieldsV2/SelectField'
import TextField from '../../FormLegacy/FormFieldsV2/TextField'
import { formValidation } from '../../FormLegacy/validation'
import ConfirmPopup from '../../Popups/ConfirmPopup'
import ManagementUserIntegrationsForm from './ManagementUserIntegrationsForm'
import {
  AllowedRole,
  AvatarField,
  FormValues,
  ManagementUser,
  ManagementUserFormProps,
  SelectedManagementUser,
} from './types'

const BUILDINGS_DROPDOWN_ALLOWED_ROLES = ['Staff', 'PropertyManager'] as const
export const INTEGRATIONS_ALLOWED_ROLES = ['Staff', 'PropertyManager', 'ShiftManager'] as const

export const ManagementUserForm = ({
  buildings,
  clearSelectedManagementUser,
  createParkingStaff,
  createPropertyManager,
  createShiftManager,
  createStaffPerson,
  createValet,
  deleteParkingStaffUser,
  deletePropertyManager,
  deleteShiftManager,
  deleteStaffPerson,
  deleteValetUser,
  selectedManagementUser,
  updateParkingStaff,
  updatePropertyManager,
  updateShiftManager,
  updateStaffPerson,
  updateValetUser,
}: ManagementUserFormProps) => {
  const { t } = useTranslation(['management-users', 'common'])
  const navigate = useNavigate()
  const { buildingId, userId } = useParams<{ buildingId?: string; userId?: string }>()

  const [selectedUser, setSelectedUser] = useState<ManagementUser>(selectedManagementUser)
  const [deletePopup, setDeletePopup] = useState<boolean>(false)
  const [formError, setFormError] = useState<string>('')

  const { displaySnackbar } = useSnackbar()

  const getProperCreateMethod = (role: AllowedRole, data: Record<string, unknown>) => {
    switch (role) {
      case 'ShiftManager':
        return createShiftManager(data)
      case 'PropertyManager':
        return createPropertyManager(data)
      case 'Staff':
        return createStaffPerson(buildingId, data)
      case 'Valet':
        return createValet(data)
      case 'ParkingStaff':
        return createParkingStaff(data)
      default:
        return Promise.resolve(null)
    }
  }

  const getProperUpdateMethod = (role: AllowedRole, data: Record<string, unknown>, id: number) => {
    switch (role) {
      case 'ShiftManager':
        return updateShiftManager(data, id)
      case 'PropertyManager':
        return updatePropertyManager(data, id)
      case 'Staff':
        return updateStaffPerson(buildingId, id, data)
      case 'Valet':
        return updateValetUser(data, id)
      case 'ParkingStaff':
        return updateParkingStaff(data, id)
      default:
        return Promise.resolve(null)
    }
  }

  const getProperDeleteMethod = async (data: NonNullable<SelectedManagementUser>) => {
    switch (data.user.role) {
      case 'ShiftManager':
        return deleteShiftManager(data.id)
      case 'PropertyManager':
        return deletePropertyManager(data.id)
      case 'Staff':
        return deleteStaffPerson(buildingId, data.id)
      case 'Valet':
        return deleteValetUser(data.id)
      case 'ParkingStaff':
        return deleteParkingStaffUser(data.id)
      default:
        return Promise.resolve(null)
    }
  }

  const getIsCreateMode = () => userId === 'new' || typeof userId === 'undefined'

  const handleFormSubmit = async (
    { avatar, buildingIds, email, firstName, id, jobTitle, lastName, phone, role }: FormValues,
    { resetForm, setSubmitting }: FormikActions<FormValues>,
  ) => {
    if (!role) return

    const isNew = getIsCreateMode()
    const dataToSend: Record<string, unknown> = {
      email,
      firstName,
      lastName,
    }

    if (role === 'Staff') {
      dataToSend.phone = phone || null
    }

    if ((BUILDINGS_DROPDOWN_ALLOWED_ROLES as readonly string[]).includes(role)) {
      dataToSend.buildingIds = buildingIds.map((b) => b.id)
    }
    if (role !== 'Valet') {
      dataToSend.jobTitle = jobTitle
    }

    if ((avatar as AvatarField).file) {
      dataToSend.avatar = (avatar as AvatarField).file
    }

    if (isNew && role === 'Staff') {
      dataToSend.notifications = 'All'
    }

    if (['PropertyManager', 'Staff', 'ShiftManager'].includes(role)) {
      dataToSend.phone = phone || null
    }

    setFormError('')

    if (isNew) {
      dataToSend.buildingId = Number(buildingId)

      try {
        setSubmitting(true)
        const res = (await getProperCreateMethod(role, dataToSend)) as {
          errCode?: number
          message?: string
        } | null
        if (res?.errCode) {
          setFormError(res.message)
        } else {
          resetForm()
          displaySnackbar(t('management-users:form.userCreated'))
          setFormError('')
        }
      } catch (err) {
        setSubmitting(false)
        if (err?.errCode === 409) {
          setFormError(t('management-users:form.userAlreadyExistsError'))
        } else {
          setFormError(err?.message || t('management-users:errors.serverError'))
        }
      }
    } else {
      try {
        setSubmitting(true)
        const updatedUserBody = (await getProperUpdateMethod(role, dataToSend, id as number)) as
          | (ManagementUser & { errCode?: number; message?: string })
          | null
        setSelectedUser(updatedUserBody)
        if (updatedUserBody?.errCode) {
          setFormError(updatedUserBody.message)
        } else {
          displaySnackbar(t('management-users:form.userUpdated'))
          setFormError('')
        }
        setSubmitting(false)
      } catch (error) {
        setSubmitting(false)
        setFormError(error?.message ?? t('management-users:errors.serverError'))
      }
    }
  }

  const handleDelete = async () => {
    if (!selectedUser) return
    try {
      await getProperDeleteMethod(selectedUser)
      displaySnackbar(t('management-users:form.userDeleted'))
      setFormError('')
      clearSelectedManagementUser()
      navigate(`/dashboard/building-dashboard/management-users/${buildingId}/new`)
    } catch {
      setFormError(t('management-users:errors.serverError'))
    } finally {
      setDeletePopup(false)
    }
  }

  const getSchema = () => {
    const schema: Record<string, unknown> = {
      buildingIds: Yup.array().when('role', (role: AllowedRole, s: Yup.Schema) => {
        if ((BUILDINGS_DROPDOWN_ALLOWED_ROLES as readonly string[]).includes(role)) {
          return Yup.array().min(1).required()
        }
        return s
      }),
      email: formValidation.email,
      firstName: formValidation.required,
      jobTitle: Yup.string(),
      lastName: formValidation.required,
      phone: Yup.string().when('role', {
        is: (role: AllowedRole) => ['PropertyManager', 'Staff', 'ShiftManager'].includes(role),
        then: formValidation.phone.required(t('management-users:form.phoneRequiredError')),
      }),
    }
    if (getIsCreateMode()) {
      schema.role = formValidation.required
    }
    return schema
  }

  const validationSchema = Yup.object().shape(getSchema())
  const isNew = getIsCreateMode()

  const getUserBuildings = (): Building[] => {
    if (!selectedUser || isNew || !selectedUser.buildingIds) {
      const defaultBuilding = buildings.find((b) => b.id === Number(buildingId))
      const selectDefaultBuilding = defaultBuilding ? [defaultBuilding] : []
      return buildings.length === 1 ? buildings : selectDefaultBuilding
    }
    return selectedUser.buildingIds
  }

  return (
    <>
      <Formik<FormValues>
        enableReinitialize
        initialValues={{
          avatar: {
            file: false,
            image:
              selectedUser && !isNew
                ? selectedUser.user.avatar
                  ? getAssetLink({ file: selectedUser.user.avatar, path: 'chitown-avatar/avatar' })
                  : ''
                : '',
          },
          buildingIds: getUserBuildings(),
          email: selectedUser && !isNew ? selectedUser.user.email : '',
          firstName: selectedUser && !isNew ? selectedUser.user.firstName : '',
          id: selectedUser && !isNew ? selectedUser.id : ('' as const),
          jobTitle: selectedUser && !isNew ? selectedUser.jobTitle || '' : '',
          lastName: selectedUser && !isNew ? selectedUser.user.lastName : '',
          phone: selectedUser && !isNew ? selectedUser.phone || '' : '',
          role: selectedUser && !isNew ? (selectedUser.user.role as AllowedRole) : null,
        }}
        validationSchema={validationSchema}
        onSubmit={handleFormSubmit}
      >
        {({
          dirty,
          errors,
          handleChange,
          handleSubmit,
          isSubmitting,
          isValid,
          setFieldTouched,
          setFieldValue,
          touched,
          submitCount,
          values,
        }) => (
          <form onSubmit={handleSubmit}>
            <div className="ctp-manager">
              <div className="ctp-manager__header">
                <h2>
                  {isNew
                    ? t('management-users:form.addNewTitle')
                    : t('management-users:form.editTitle')}
                </h2>
              </div>
              <div className="form form--object-profile">
                <div className="column">
                  <div className="columns">
                    <div className="column">
                      <ImageField
                        imageRatio={1}
                        label={t('management-users:form.avatarLabel')}
                        name="avatar"
                        placeholderImage={getAssetLink({
                          file: 'avatar.png',
                          path: 'chitown-avatar/avatar',
                        })}
                        value={values.avatar}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  <div className="columns">
                    <div className="column">
                      <TextField
                        errorLabel={touched.firstName && (errors.firstName as string)}
                        isError={!!(touched.firstName && errors.firstName)}
                        label={t('management-users:form.firstNameLabel')}
                        name="firstName"
                        placeholder={t('management-users:form.firstNamePlaceholder')}
                        value={values.firstName}
                        onBlur={() => setFieldTouched('firstName')}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="column">
                      <TextField
                        errorLabel={touched.lastName && (errors.lastName as string)}
                        isError={!!(touched.lastName && errors.lastName)}
                        label={t('management-users:form.lastNameLabel')}
                        name="lastName"
                        placeholder={t('management-users:form.lastNamePlaceholder')}
                        value={values.lastName}
                        onBlur={() => setFieldTouched('lastName')}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <TextField
                    disabled={!isNew}
                    errorLabel={touched.email && (errors.email as string)}
                    isError={!!(touched.email && errors.email)}
                    label={t('management-users:form.emailLabel')}
                    name="email"
                    placeholder={t('management-users:form.emailPlaceholder')}
                    value={values.email}
                    onBlur={() => setFieldTouched('email')}
                    onChange={handleChange}
                  />

                  {isNew && (
                    <SelectField
                      isError={!!(touched.role && errors.role)}
                      label={t('management-users:form.roleLabel')}
                      name="role"
                      options={[
                        {
                          label: t('management-users:roleLabels.propertyManager'),
                          value: 'PropertyManager',
                        },
                        {
                          label: t('management-users:roleLabels.frontDesk'),
                          value: 'ShiftManager',
                        },
                        { label: t('management-users:roleLabels.maintenance'), value: 'Staff' },
                        { label: t('management-users:roleLabels.valet'), value: 'Valet' },
                        {
                          label: t('management-users:roleLabels.parkingStaff'),
                          value: 'ParkingStaff',
                        },
                      ]}
                      placeholder={t('management-users:form.roleSelectPlaceholder')}
                      value={values.role}
                      onChange={(e: { target: { value: string } }) =>
                        setFieldValue('role', e.target.value)
                      }
                    />
                  )}

                  {values.role &&
                  (BUILDINGS_DROPDOWN_ALLOWED_ROLES as readonly string[]).includes(values.role) ? (
                    <Filter
                      sortSelectedFirst
                      additionalClass="field form--object-profile__building-dropdown"
                      config={{ hasSearch: true, multiple: true }}
                      label={t('management-users:form.buildingLabel')}
                      name="buildingIds"
                      options={buildings}
                      placeholder={
                        values.buildingIds.length > 0
                          ? values.buildingIds.map((b) => b.name).join(', ')
                          : t('management-users:form.buildingPlaceholder')
                      }
                      value={values.buildingIds}
                      onChange={(e: { target: { name: string; value: Building[] | Building } }) => {
                        if (buildings.length === 1) return
                        handleChange(e)
                      }}
                    />
                  ) : null}

                  {values.role !== 'Valet' && values.role !== 'ParkingStaff' ? (
                    <TextField
                      errorLabel={touched.jobTitle && (errors.jobTitle as string)}
                      isError={!!(touched.jobTitle && errors.jobTitle)}
                      label={t('management-users:form.jobTitleLabel')}
                      name="jobTitle"
                      placeholder={t('management-users:form.jobTitlePlaceholder')}
                      value={values.jobTitle || ''}
                      onBlur={() => setFieldTouched('jobTitle')}
                      onChange={handleChange}
                    />
                  ) : null}

                  {values.role &&
                  ['PropertyManager', 'Staff', 'ShiftManager'].includes(values.role) ? (
                    <PhoneField
                      errorLabel={touched.phone && (errors.phone as string)}
                      isError={!!(touched.phone && errors.phone)}
                      label={t('management-users:form.phoneLabel')}
                      name="phone"
                      placeholder={t('management-users:form.phonePlaceholder')}
                      value={values.phone}
                      onBlur={() => setFieldTouched('phone')}
                      onChange={handleChange}
                    />
                  ) : null}
                </div>

                {selectedUser &&
                  !isNew &&
                  INTEGRATIONS_ALLOWED_ROLES.includes(values.role as AllowedRole) && (
                    <ManagementUserIntegrationsForm
                      managementUserData={selectedUser}
                      userBuildings={getUserBuildings()}
                    />
                  )}

                <div className="columns form__actions">
                  <div className="column">
                    <button
                      className={`button button-primary ${isSubmitting ? 'is-loading' : ''}`}
                      disabled={!dirty || isSubmitting || (submitCount > 0 && !isValid)}
                      type="submit"
                    >
                      {isNew ? t('common:actions.save') : t('management-users:form.updateButton')}
                    </button>
                  </div>

                  {!isNew && (
                    <div className="column">
                      <DeleteButton
                        callback={() => setDeletePopup(true)}
                        pending={isSubmitting}
                        text={t('common:actions.delete')}
                      />
                    </div>
                  )}
                  <label className="help is-danger">{formError}</label>
                </div>
              </div>
            </div>
          </form>
        )}
      </Formik>

      <ConfirmPopup
        autoWidth
        cancelCallback={() => setDeletePopup(false)}
        cancelText={t('common:actions.cancel')}
        confirmCallback={handleDelete}
        confirmText={t('management-users:form.confirmContinueText')}
        isOpen={deletePopup}
        title={t('management-users:form.deleteConfirmTitle')}
      />
    </>
  )
}

const mapStateToProps = (state: RootState) => {
  const buildings = (selectBuildingsForFilter(state) || []) as Building[]
  return { buildings }
}

const mapDispatch = {
  createParkingStaff,
  createPropertyManager,
  createShiftManager,
  createStaffPerson,
  createValet,
  deleteParkingStaffUser,
  deletePropertyManager,
  deleteShiftManager,
  deleteStaffPerson,
  deleteValetUser,
  updateParkingStaff,
  updatePropertyManager,
  updateShiftManager,
  updateStaffPerson,
  updateValetUser,
}

export default connect(mapStateToProps, mapDispatch)(ManagementUserForm)
